import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  useMediaQuery,
  TablePagination,
  Tooltip,
} from "@mui/material";
import colors from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUpward,
  ArrowDownward,
  Add,
  LocationOn,
  MoreVert,
} from "@mui/icons-material";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import theme from "../../Constants/theme";
import { entityIcons } from "../../Constants/entityIcons";
import React from "react";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { getEntityIconByName } from "../../Helpers/getEntityIcon";
import { useTableParams } from "../../Core/useTableParams";
import { useTerminology } from "../../Core/useTerminology";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import CompanyImportModal from "../EntityPage/CompanyImportModal";
import {
  useCompanies,
  useDeleteCompany,
  useWorkspaceShareCompany,
} from "../../Query/useCompanies";
import { UserRole } from "../../Enums/companyStatus";
import { hasRole } from "../../Helpers/authHelpers";
import { ComoSwitch } from "../Common/ComoSwitch";
import { useCurrentWorkspace } from "../../Query/useWorkspace";

const Companies = () => {
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();
  const { t } = useTranslation("companiesPage");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const terminology = useTerminology();
  const [showWorkspaceSharedOnly, setShowWorkspaceSharedOnly] = useState(false);
  const isAdmin = hasRole(UserRole.ADMIN);

  const { page, pageSize, sortColumn, sortOrder, setPage, setSort } =
    useTableParams<"name">({
      defaultSortColumn: "name",
      defaultSortOrder: "asc",
      defaultPage: 1,
      defaultSearch: "",
      defaultPageSize: 10,
    });

  const [menuOpenCompanyId, setMenuOpenCompanyId] = useState<string | null>(
    null,
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    null,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [companyIdToDelete, setCompanyIdToDelete] = useState<string | null>(
    null,
  );
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { data: currentWorkspace } = useCurrentWorkspace();
  const hasWorkspace = !!currentWorkspace?.workspace?.id;

  const { data: companiesData, isLoading } = useCompanies(
    page,
    pageSize,
    sortColumn,
    sortOrder,
    "",
    showWorkspaceSharedOnly,
  );

  const companies = companiesData?.companies ?? [];
  const total = companiesData?.total ?? 0;

  const deleteCompanyMutation = useDeleteCompany();

  const workspaceShareMutation = useWorkspaceShareCompany();

  const handleOpenImportModal = () => setImportModalOpen(true);
  const handleCloseImportModal = () => setImportModalOpen(false);

  const handleImportSuccess = (result: any) => {
    if (result?.company_id) {
      navigate(`/entity/${result.company_id}`);
    }
  };

  const handleDeleteDialogClose = (confirmed: boolean) => {
    setIsDeleteDialogOpen(false);
    if (confirmed && companyIdToDelete) {
      deleteCompanyMutation.mutate(companyIdToDelete);
    }
    setCompanyIdToDelete(null);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    companyId: string,
  ) => {
    event.stopPropagation();
    setMenuOpenCompanyId(companyId);
    setSelectedCompanyId(companyId);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpenCompanyId(null);
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (isLoading && (!companies || companies.length === 0)) {
    return (
      <Loading
        backgroundColor={colors.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
    >
      <Box sx={{ maxWidth: "1280px", mx: "auto", px: 3, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${colors.primary_dark}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant={isMobile ? "h4" : "h3"}
                sx={{
                  fontWeight: 600,
                  color: colors.primary_dark,
                  marginBottom: 0,
                }}
              >
                {t("title", { entity: terminology.plural })}
              </Typography>
              <Box sx={{ position: "relative" }} ref={menuRef}>
                <Tooltip
                  title={t("createOrImport", { entity: terminology.singular })}
                >
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen((prev) => !prev);
                    }}
                    sx={{
                      color: colors.accent,
                      p: 0.5,
                      "&:hover": { backgroundColor: `${colors.accent}15` },
                    }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
                {isMenuOpen && (
                  <Paper
                    elevation={3}
                    sx={{
                      position: "absolute",
                      zIndex: 10,
                      top: 0,
                      left: "100%",
                      ml: 0.5,
                      minWidth: 180,
                      borderRadius: 2,
                      border: `1px solid ${colors.gray200}`,
                      borderLeft: `4px solid ${colors.primary_dark}`,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/entity/create");
                        setMenuOpen(false);
                      }}
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: "pointer",
                        bgcolor: "white",
                        borderLeft: "4px solid transparent",
                        "&:hover": { bgcolor: colors.gray50 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: colors.primary_dark,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("createNew", { entity: terminology.singular })}
                      </Typography>
                    </Box>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenImportModal();
                        setMenuOpen(false);
                      }}
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: "pointer",
                        bgcolor: "white",
                        borderLeft: "4px solid transparent",
                        "&:hover": { bgcolor: colors.gray50 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: colors.primary_dark,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("importExisting", { entity: terminology.singular })}
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Box>

            {companies.length > 0 && !isAdmin && (
              <Tooltip
                title={
                  showWorkspaceSharedOnly
                    ? t("showAllEntitiesTooltip", {
                        entity: terminology.plural,
                      })
                    : t("showWorkspaceSharedOnlyTooltip", {
                        entity: terminology.plural,
                      })
                }
                enterDelay={0}
                disableInteractive
              >
                <span style={{ display: "inline-block" }}>
                  <ComoSwitch
                    checked={showWorkspaceSharedOnly}
                    onChange={() => setShowWorkspaceSharedOnly((prev) => !prev)}
                  />
                </span>
              </Tooltip>
            )}
          </Box>
        </Paper>

        {companies.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSort("name")}
              endIcon={
                sortOrder === "asc" ? <ArrowUpward /> : <ArrowDownward />
              }
              sx={{
                borderColor: colors.gray200,
                color: colors.gray700,
                textTransform: "none",
                backgroundColor: colors.white,
                "&:hover": {
                  borderColor: colors.primary_dark,
                  backgroundColor: colors.gray50,
                },
              }}
            >
              {t("sortByName")}
            </Button>
            {companies.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={total}
                rowsPerPage={pageSize}
                page={page - 1}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                sx={{
                  "& .MuiTablePagination-displayedRows": {
                    backgroundColor: colors.white,
                    fontWeight: 500,
                    fontSize: "0.8125rem",
                    border: `1px solid ${colors.gray200}`,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    color: colors.primary_dark,
                    margin: 0,
                  },
                  color: colors.primary_dark,
                  "& .MuiTablePagination-toolbar": {
                    color: colors.primary_dark,
                  },
                }}
              />
            )}
          </Box>
        )}

        {companies.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr",
                md: "repeat(2, 1fr)",
              },
              gap: 3,
            }}
          >
            {companies.map((company) => (
              <Paper
                key={company.id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${colors.gray200}`,
                  borderLeft: `4px solid ${colors.accent}`,
                  cursor: "pointer",
                }}
                onClick={() => navigate(`/entity/${company.id}`)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: colors.primary_dark,
                          marginBottom: 0,
                        }}
                      >
                        {company.name}
                      </Typography>
                      <Box
                        sx={{
                          color: colors.accent,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {React.cloneElement(
                          getEntityIconByName(
                            entityIcons[company.icon_id - 1]?.iconName,
                          ) || <></>,
                          { sx: { fontSize: "2rem" } },
                        )}
                      </Box>
                      {company.workspace_shared && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: colors.primary_dark,
                            color: colors.white,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontWeight: 500,
                          }}
                        >
                          {t("workspaceShared")}
                        </Typography>
                      )}
                    </Box>

                    {company.industry && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1.5,
                        }}
                      >
                        <BusinessRoundedIcon
                          sx={{ color: colors.accent, fontSize: "1.25rem" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: colors.gray700 }}
                        >
                          {company.industry}
                        </Typography>
                      </Box>
                    )}

                    {company.location && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <LocationOn
                          sx={{ color: colors.accent, fontSize: "1.25rem" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: colors.gray700 }}
                        >
                          {company.location}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{ position: "relative" }}
                    ref={
                      menuOpenCompanyId === company.id.toString()
                        ? menuRef
                        : undefined
                    }
                  >
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, company.id.toString());
                      }}
                      sx={{ "&:hover": { backgroundColor: colors.gray100 } }}
                    >
                      <MoreVert sx={{ color: colors.gray500 }} />
                    </IconButton>
                    {menuOpenCompanyId === company.id.toString() && (
                      <Paper
                        elevation={3}
                        sx={{
                          position: "absolute",
                          zIndex: 10,
                          right: 0,
                          top: "100%",
                          mt: 0.5,
                          minWidth: 180,
                          borderRadius: 2,
                          border: `1px solid ${colors.gray200}`,
                          borderLeft: `4px solid ${colors.primary_dark}`,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            if (selectedCompanyId)
                              navigate(`/entity/${selectedCompanyId}`);
                            setMenuOpenCompanyId(null);
                          }}
                          sx={{
                            px: 2,
                            py: 1.5,
                            cursor: "pointer",
                            bgcolor: "white",
                            borderLeft: "4px solid transparent",
                            "&:hover": { bgcolor: colors.gray50 },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: colors.primary_dark,
                            }}
                          >
                            {t("edit", { entity: terminology.genitive })}
                          </Typography>
                        </Box>
                        {!isAdmin &&
                          hasWorkspace &&
                          !company.workspace_shared && (
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                if (selectedCompanyId) {
                                  workspaceShareMutation.mutate(
                                    selectedCompanyId,
                                  );
                                }
                                setMenuOpenCompanyId(null);
                              }}
                              sx={{
                                px: 2,
                                py: 1.5,
                                cursor: "pointer",
                                bgcolor: "white",
                                borderLeft: "4px solid transparent",
                                "&:hover": { bgcolor: colors.gray50 },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: colors.primary_dark,
                                }}
                              >
                                {t("shareWorkspace", {
                                  entity: terminology.singular,
                                })}
                              </Typography>
                            </Box>
                          )}
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            if (selectedCompanyId) {
                              setCompanyIdToDelete(selectedCompanyId);
                              setIsDeleteDialogOpen(true);
                            }
                            setMenuOpenCompanyId(null);
                          }}
                          sx={{
                            px: 2,
                            py: 1.5,
                            cursor: "pointer",
                            bgcolor: "white",
                            borderLeft: "4px solid transparent",
                            "&:hover": { bgcolor: colors.gray50 },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: colors.red600,
                            }}
                          >
                            {t("delete", { entity: terminology.singular })}
                          </Typography>
                        </Box>
                      </Paper>
                    )}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 2,
              border: `1px solid ${colors.gray200}`,
              textAlign: "center",
            }}
          >
            <Typography variant="h5" sx={{ color: colors.gray700, mb: 3 }}>
              {t("noCompanies", { entity: terminology.plural })}
            </Typography>
            <Button
              variant="modern"
              startIcon={<Add />}
              onClick={() => navigate("/entity/create")}
            >
              {t("createButton", { entity: terminology.singular })}
            </Button>
          </Paper>
        )}

        <ConfirmationDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          title="Delete Company"
          description="Confirm company deletion"
          content="Are you sure you want to delete this company? This action cannot be undone."
          actions={[
            {
              label: "Cancel",
              onClick: () => handleDeleteDialogClose(false),
            },
            {
              label: "Delete",
              onClick: () => handleDeleteDialogClose(true),
            },
          ]}
        />
        {isImportModalOpen && (
          <CompanyImportModal
            open={isImportModalOpen}
            onClose={handleCloseImportModal}
            onImported={handleImportSuccess}
          />
        )}
      </Box>
    </Box>
  );
};

export default Companies;
